import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Paperclip, Plus, Printer, Trash2, X } from 'lucide-react';
import { Button, Skeleton, StatusBadge } from '@/components/ui';
import { Input } from '@/components/ui/input';
import type { StatusKey } from '@/types';
import { getApiErrorMessage } from '@/lib/axios';
import { uploadFile } from '@/services/uploads';
import {
  type AvanceStatut,
  type EmployeDTO,
  type TypeContrat,
  type VisiteResultat,
  type VisiteType,
  AVANCE_STATUTS,
  TYPES_CONTRAT,
  VISITE_RESULTATS,
  VISITE_TYPES,
  createAvance,
  createContrat,
  createVisite,
  deleteAvance,
  deleteContrat,
  deleteVisite,
  listAvances,
  listBulletins,
  listContrats,
  listEmployes,
  listVisites,
  openBulletinPdf,
  updateAvance,
} from '@/services/rh';
import { toast } from '@/store/toastStore';
import { formatDZD } from '@/utils/algeria';

const selectCls = 'flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm';
const today = () => new Date().toISOString().slice(0, 10);

const CONTRAT_LABEL: Record<TypeContrat, string> = { CDI: 'CDI', CDD: 'CDD', ANAPEC: 'ANAPEC', INTERIM: 'Intérim' };
const AVANCE_UI: Record<AvanceStatut, { label: string; status: StatusKey }> = {
  DEMANDEE: { label: 'Demandée', status: 'pending' },
  ACCORDEE: { label: 'Accordée', status: 'working' },
  REMBOURSEE: { label: 'Remboursée', status: 'done' },
  REFUSEE: { label: 'Refusée', status: 'stuck' },
};
const VISITE_TYPE_LABEL: Record<VisiteType, string> = { EMBAUCHE: 'Embauche', PERIODIQUE: 'Périodique', REPRISE: 'Reprise', SPONTANEE: 'Spontanée' };
const VISITE_RES_UI: Record<VisiteResultat, { label: string; status: StatusKey }> = {
  APTE: { label: 'Apte', status: 'done' },
  APTE_AVEC_RESERVE: { label: 'Apte avec réserve', status: 'working' },
  INAPTE: { label: 'Inapte', status: 'stuck' },
  EN_ATTENTE: { label: 'En attente', status: 'pending' },
};

function useEmployesMap() {
  const { data } = useQuery({ queryKey: ['employes'], queryFn: () => listEmployes() });
  const map = useMemo(() => {
    const m = new Map<string, string>();
    data?.items.forEach((e) => m.set(e.id, `${e.matricule} · ${e.prenom} ${e.nom}`));
    return m;
  }, [data]);
  return { employes: data?.items ?? [], nomEmploye: (id: string) => map.get(id) ?? '—' };
}

function EmployeSelect({ employes, value, onChange }: { employes: EmployeDTO[]; value: string; onChange: (v: string) => void }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={selectCls}>
      <option value="">— Sélectionner un employé —</option>
      {employes.map((e) => <option key={e.id} value={e.id}>{e.matricule} · {e.prenom} {e.nom}</option>)}
    </select>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex min-h-[24vh] flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface-secondary text-center">
      <p className="px-6 text-sm text-content-secondary">{message}</p>
    </div>
  );
}

/* ----------------------------- Fiches de paie ----------------------------- */
export function FichesPaieTab() {
  const now = new Date();
  const [mois, setMois] = useState<number | ''>('');
  const [annee, setAnnee] = useState<number>(now.getFullYear());
  const { nomEmploye } = useEmployesMap();
  const { data, isLoading } = useQuery({
    queryKey: ['bulletins', mois, annee],
    queryFn: () => listBulletins({ mois: mois || undefined, annee }),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <select value={mois} onChange={(e) => setMois(e.target.value ? Number(e.target.value) : '')} className={selectCls + ' w-auto'}>
          <option value="">Tous les mois</option>
          {Array.from({ length: 12 }, (_, i) => <option key={i} value={i + 1}>{i + 1}</option>)}
        </select>
        <Input type="number" step="1" value={annee} onChange={(e) => setAnnee(Number(e.target.value))} className="w-28" />
      </div>
      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : data && data.items.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary">
              <tr className="border-b border-border text-left">
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Employé</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Période</th>
                <th className="px-4 py-2.5 text-right font-semibold text-content-secondary">Brut imposable</th>
                <th className="px-4 py-2.5 text-right font-semibold text-content-secondary">Net à payer</th>
                <th className="px-4 py-2.5 text-right font-semibold text-content-secondary">Coût employeur</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {data.items.map((b) => (
                <tr key={b.id} className="border-b border-border last:border-0 hover:bg-surface-hover">
                  <td className="px-4 py-2.5 font-medium text-content-primary">{nomEmploye(b.employe_id)}</td>
                  <td className="px-4 py-2.5 tabular-nums text-content-secondary">{b.mois.toString().padStart(2, '0')}/{b.annee}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{formatDZD(b.brut_imposable)}</td>
                  <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-brand-primary">{formatDZD(b.net_a_payer)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-content-secondary">{formatDZD(b.cout_total_employeur)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => openBulletinPdf(b.id, 'open').catch((e) => toast.error(getApiErrorMessage(e)))} className="rounded p-1.5 text-content-muted hover:bg-surface-hover hover:text-brand-primary" aria-label="Imprimer le bulletin">
                        <Printer className="size-4" />
                      </button>
                      <button onClick={() => openBulletinPdf(b.id, 'download').catch((e) => toast.error(getApiErrorMessage(e)))} className="rounded p-1.5 text-content-muted hover:bg-surface-hover hover:text-brand-primary" aria-label="Télécharger le bulletin">
                        <Download className="size-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState message="Aucune fiche de paie pour cette période. Calculez un bulletin dans l'onglet « Calcul de paie »." />
      )}
    </div>
  );
}

/* -------------------------------- Contrats -------------------------------- */
export function ContratsTab() {
  const queryClient = useQueryClient();
  const { employes, nomEmploye } = useEmployesMap();
  const [form, setForm] = useState({ employe_id: '', reference: '', type_contrat: 'CDI', poste: '', salaire_base: '', date_debut: today(), date_fin: '' });
  const [doc, setDoc] = useState<{ url: string; filename: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const { data, isLoading } = useQuery({ queryKey: ['contrats'], queryFn: () => listContrats() });

  const handleFile = async (file: File) => {
    setUploading(true);
    try {
      setDoc(await uploadFile(file));
      toast.success('Contrat scanné joint.');
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setUploading(false);
    }
  };

  const addMut = useMutation({
    mutationFn: () => createContrat({
      employe_id: form.employe_id,
      reference: form.reference.trim() || null,
      type_contrat: form.type_contrat as TypeContrat,
      poste: form.poste || null,
      salaire_base: Number(form.salaire_base) || 0,
      date_debut: form.date_debut || null,
      date_fin: form.date_fin || null,
      actif: true,
      document_url: doc?.url ?? null,
      notes: null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contrats'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success('Contrat ajouté.');
      setForm((f) => ({ ...f, reference: '', poste: '', salaire_base: '', date_fin: '' }));
      setDoc(null);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => deleteContrat(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contrats'] }),
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
        <h2 className="mb-3 text-base font-bold text-content-primary">Nouveau contrat</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <EmployeSelect employes={employes} value={form.employe_id} onChange={(v) => setForm({ ...form, employe_id: v })} />
          <Input placeholder="Référence (ex. CDI-2026-001)" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
          <select value={form.type_contrat} onChange={(e) => setForm({ ...form, type_contrat: e.target.value })} className={selectCls}>
            {TYPES_CONTRAT.map((c) => <option key={c} value={c}>{CONTRAT_LABEL[c]}</option>)}
          </select>
          <Input placeholder="Poste" value={form.poste} onChange={(e) => setForm({ ...form, poste: e.target.value })} />
          <Input type="number" step="1" placeholder="Salaire de base" value={form.salaire_base} onChange={(e) => setForm({ ...form, salaire_base: e.target.value })} />
          <Input type="date" value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} />
          <Input type="date" value={form.date_fin} onChange={(e) => setForm({ ...form, date_fin: e.target.value })} />
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
          {doc ? (
            <div className="flex h-10 items-center justify-between rounded-md border border-status-done/40 bg-status-done/10 px-3 text-sm">
              <span className="flex items-center gap-2 truncate text-content-primary"><Paperclip className="size-4 shrink-0" /> <span className="truncate">{doc.filename}</span></span>
              <button type="button" onClick={() => setDoc(null)} className="rounded p-1 text-content-muted hover:text-status-stuck" aria-label="Retirer"><X className="size-4" /></button>
            </div>
          ) : (
            <Button type="button" variant="secondary" className="w-full" onClick={() => fileRef.current?.click()} loading={uploading}>
              <Paperclip /> Joindre le contrat scanné
            </Button>
          )}
        </div>
        <div className="mt-3 flex justify-end">
          <Button onClick={() => addMut.mutate()} loading={addMut.isPending} disabled={!form.employe_id}><Plus /> Ajouter le contrat</Button>
        </div>
      </div>

      {isLoading ? <Skeleton className="h-48 w-full" /> : data && data.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary">
              <tr className="border-b border-border text-left">
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Employé</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Référence</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Type</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Poste</th>
                <th className="px-4 py-2.5 text-right font-semibold text-content-secondary">Salaire</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Début</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Fin</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Contrat scanné</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {data.map((c) => (
                <tr key={c.id} className="border-b border-border last:border-0 hover:bg-surface-hover">
                  <td className="px-4 py-2.5 font-medium text-content-primary">{nomEmploye(c.employe_id)}</td>
                  <td className="px-4 py-2.5 text-content-secondary">{c.reference ?? '—'}</td>
                  <td className="px-4 py-2.5">{CONTRAT_LABEL[c.type_contrat]}</td>
                  <td className="px-4 py-2.5 text-content-secondary">{c.poste ?? '—'}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{formatDZD(c.salaire_base)}</td>
                  <td className="px-4 py-2.5 tabular-nums text-content-secondary">{c.date_debut ?? '—'}</td>
                  <td className="px-4 py-2.5 tabular-nums text-content-secondary">{c.date_fin ?? '—'}</td>
                  <td className="px-4 py-2.5">
                    {c.document_url ? (
                      <a href={c.document_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-medium text-brand-primary hover:underline">
                        <FileText className="size-4" /> Voir
                      </a>
                    ) : <span className="text-content-muted">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => delMut.mutate(c.id)} className="rounded p-1.5 text-content-muted hover:bg-status-stuck/10 hover:text-status-stuck" aria-label="Supprimer"><Trash2 className="size-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <EmptyState message="Aucun contrat enregistré." />}
    </div>
  );
}

/* ----------------------------- Avances salaire ---------------------------- */
export function AvancesTab() {
  const queryClient = useQueryClient();
  const { employes, nomEmploye } = useEmployesMap();
  const [form, setForm] = useState({ employe_id: '', date_avance: today(), montant: '', motif: '', mode_paiement: 'ESPECES', statut: 'ACCORDEE' });
  const { data, isLoading } = useQuery({ queryKey: ['avances'], queryFn: () => listAvances() });

  const addMut = useMutation({
    mutationFn: () => createAvance({
      employe_id: form.employe_id,
      date_avance: form.date_avance,
      montant: Number(form.montant) || 0,
      motif: form.motif || null,
      mode_paiement: form.mode_paiement as EmployeDTO['mode_paiement'],
      statut: form.statut as AvanceStatut,
      date_remboursement: null,
      notes: null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['avances'] });
      toast.success('Avance enregistrée.');
      setForm((f) => ({ ...f, montant: '', motif: '' }));
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const updMut = useMutation({
    mutationFn: ({ id, statut }: { id: string; statut: AvanceStatut }) => updateAvance(id, { statut }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['avances'] }),
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => deleteAvance(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['avances'] }),
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
        <h2 className="mb-3 text-base font-bold text-content-primary">Nouvelle avance sur salaire</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <EmployeSelect employes={employes} value={form.employe_id} onChange={(v) => setForm({ ...form, employe_id: v })} />
          <Input type="date" value={form.date_avance} onChange={(e) => setForm({ ...form, date_avance: e.target.value })} />
          <Input type="number" step="1" placeholder="Montant (DZD)" value={form.montant} onChange={(e) => setForm({ ...form, montant: e.target.value })} />
          <Input className="lg:col-span-2" placeholder="Motif" value={form.motif} onChange={(e) => setForm({ ...form, motif: e.target.value })} />
          <select value={form.statut} onChange={(e) => setForm({ ...form, statut: e.target.value })} className={selectCls}>
            {AVANCE_STATUTS.map((s) => <option key={s} value={s}>{AVANCE_UI[s].label}</option>)}
          </select>
        </div>
        <div className="mt-3 flex justify-end">
          <Button onClick={() => addMut.mutate()} loading={addMut.isPending} disabled={!form.employe_id || !form.montant}><Plus /> Ajouter l'avance</Button>
        </div>
      </div>

      {isLoading ? <Skeleton className="h-48 w-full" /> : data && data.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary">
              <tr className="border-b border-border text-left">
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Employé</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Date</th>
                <th className="px-4 py-2.5 text-right font-semibold text-content-secondary">Montant</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Motif</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Statut</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {data.map((a) => (
                <tr key={a.id} className="border-b border-border last:border-0 hover:bg-surface-hover">
                  <td className="px-4 py-2.5 font-medium text-content-primary">{nomEmploye(a.employe_id)}</td>
                  <td className="px-4 py-2.5 tabular-nums text-content-secondary">{a.date_avance}</td>
                  <td className="px-4 py-2.5 text-right font-semibold tabular-nums">{formatDZD(a.montant)}</td>
                  <td className="px-4 py-2.5 text-content-secondary">{a.motif ?? '—'}</td>
                  <td className="px-4 py-2.5">
                    <select value={a.statut} onChange={(e) => updMut.mutate({ id: a.id, statut: e.target.value as AvanceStatut })} className="h-8 rounded-md border border-border bg-surface-secondary px-2 text-xs font-semibold">
                      {AVANCE_STATUTS.map((s) => <option key={s} value={s}>{AVANCE_UI[s].label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => delMut.mutate(a.id)} className="rounded p-1.5 text-content-muted hover:bg-status-stuck/10 hover:text-status-stuck" aria-label="Supprimer"><Trash2 className="size-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <EmptyState message="Aucune avance sur salaire." />}
    </div>
  );
}

/* ---------------------------- Visites médicales --------------------------- */
export function VisitesTab() {
  const queryClient = useQueryClient();
  const { employes, nomEmploye } = useEmployesMap();
  const [form, setForm] = useState({ employe_id: '', date_visite: today(), type: 'PERIODIQUE', resultat: 'APTE', medecin: '', date_prochaine: '' });
  const { data, isLoading } = useQuery({ queryKey: ['visites'], queryFn: () => listVisites() });

  const addMut = useMutation({
    mutationFn: () => createVisite({
      employe_id: form.employe_id,
      date_visite: form.date_visite,
      type: form.type as VisiteType,
      resultat: form.resultat as VisiteResultat,
      medecin: form.medecin || null,
      date_prochaine: form.date_prochaine || null,
      observations: null,
      document_url: null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visites'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success('Visite médicale ajoutée.');
      setForm((f) => ({ ...f, medecin: '', date_prochaine: '' }));
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => deleteVisite(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['visites'] }),
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
        <h2 className="mb-3 text-base font-bold text-content-primary">Nouvelle visite médicale</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <EmployeSelect employes={employes} value={form.employe_id} onChange={(v) => setForm({ ...form, employe_id: v })} />
          <Input type="date" value={form.date_visite} onChange={(e) => setForm({ ...form, date_visite: e.target.value })} />
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={selectCls}>
            {VISITE_TYPES.map((t) => <option key={t} value={t}>{VISITE_TYPE_LABEL[t]}</option>)}
          </select>
          <select value={form.resultat} onChange={(e) => setForm({ ...form, resultat: e.target.value })} className={selectCls}>
            {VISITE_RESULTATS.map((r) => <option key={r} value={r}>{VISITE_RES_UI[r].label}</option>)}
          </select>
          <Input placeholder="Médecin" value={form.medecin} onChange={(e) => setForm({ ...form, medecin: e.target.value })} />
          <div>
            <label className="mb-1 block text-xs text-content-muted">Prochaine visite</label>
            <Input type="date" value={form.date_prochaine} onChange={(e) => setForm({ ...form, date_prochaine: e.target.value })} />
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button onClick={() => addMut.mutate()} loading={addMut.isPending} disabled={!form.employe_id}><Plus /> Ajouter la visite</Button>
        </div>
      </div>

      {isLoading ? <Skeleton className="h-48 w-full" /> : data && data.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary">
              <tr className="border-b border-border text-left">
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Employé</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Date</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Type</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Résultat</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Médecin</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Prochaine</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {data.map((v) => {
                const r = VISITE_RES_UI[v.resultat];
                return (
                  <tr key={v.id} className="border-b border-border last:border-0 hover:bg-surface-hover">
                    <td className="px-4 py-2.5 font-medium text-content-primary">{nomEmploye(v.employe_id)}</td>
                    <td className="px-4 py-2.5 tabular-nums text-content-secondary">{v.date_visite}</td>
                    <td className="px-4 py-2.5 text-content-secondary">{VISITE_TYPE_LABEL[v.type]}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={r.status} label={r.label} /></td>
                    <td className="px-4 py-2.5 text-content-secondary">{v.medecin ?? '—'}</td>
                    <td className="px-4 py-2.5 tabular-nums text-content-secondary">{v.date_prochaine ?? '—'}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => delMut.mutate(v.id)} className="rounded p-1.5 text-content-muted hover:bg-status-stuck/10 hover:text-status-stuck" aria-label="Supprimer"><Trash2 className="size-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <EmptyState message="Aucune visite médicale enregistrée." />}
    </div>
  );
}
